import { useEffect, useState, useMemo, useCallback } from "react";
import {
  UserPlus,
  Pencil,
  X,
  Building,
  Building2,
  Search,
  RotateCw,
  KeyRound,
  UserCheck,
  UserX,
  Trash2,
  RotateCcw,
  ShieldAlert,
  MoreVertical,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  DEPARTMENTS,
  isSuperAdmin,
  isAdmin,
  formatRole,
  getUserCompanies,
  formatUserCompanies,
} from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import PasswordInput from "../ui/PasswordInput";
import MultiSelectDepartments from "../ui/MultiSelectDepartments";
import MultiSelectCompanies from "../ui/MultiSelectCompanies";
import { useSavedState } from "../../lib/listState";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function roleColor(role) {
  const r = (role || "user").toLowerCase();
  if (r === "super_admin") return { bg: "#f3e8ff", color: "#6d28d9", border: "#c4b5fd" };
  if (r === "admin") return { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" };
  return { bg: "#ecfdf5", color: "#047857", border: "#6ee7b7" };
}

function initials(name) {
  return (name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function getDeptTagClass(dept) {
  const d = (dept || "").toLowerCase().trim();
  if (d === "sales") return "dept-tag sales";
  if (d === "design") return "dept-tag design";
  if (d === "production") return "dept-tag production";
  if (d === "dispatch") return "dept-tag dispatch";
  if (d === "delivery") return "dept-tag delivery";
  if (d === "accounts") return "dept-tag accounts";
  if (d === "procurement") return "dept-tag procurement";
  if (d === "management") return "dept-tag management";
  return "dept-tag sales";
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function UsersRoles({ user, onOpenUser }) {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useSavedState("users_roles", "roleFilter", "");
  const [deptFilter, setDeptFilter] = useSavedState("users_roles", "deptFilter", "");
  const [companyFilter, setCompanyFilter] = useSavedState("users_roles", "companyFilter", "");
  const [statusFilter, setStatusFilter] = useSavedState("users_roles", "statusFilter", "all");
  const [searchQuery, setSearchQuery] = useSavedState("users_roles", "searchQuery", "");

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset password modal
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  // Delete user modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Three-dots action dropdown state: { user, top, right, openUpwards }
  const [activeMenu, setActiveMenu] = useState(null);

  // Close three-dots dropdown on outside click, escape key, scroll or resize
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".user-action-dropdown") && !e.target.closest(".user-action-dots-btn")) {
        setActiveMenu(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveMenu(null);
      }
    };
    const handleScrollOrResize = () => {
      setActiveMenu(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, []);

  const currentUserIsSuperAdmin = isSuperAdmin(user);
  const currentUserIsAdmin = isAdmin(user);

  // Form states
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", password: "", role: "user", departments: [], companies: [],
  });
  const [formErrors, setFormErrors] = useState({});

  const [activeCompanies, setActiveCompanies] = useState([]);

  const loadActiveCompanies = useCallback(async () => {
    try {
      const res = await api("get", "/companies?active_only=true");
      if (Array.isArray(res.data)) {
        const names = res.data.map((c) => (typeof c === "string" ? c : c.name || c.company_name)).filter(Boolean);
        setActiveCompanies(names);
      }
    } catch (err) {
      console.error("Failed to load active companies:", err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const [usersRes] = await Promise.all([
        api("get", `/users${qs}`),
        loadActiveCompanies(),
      ]);
      const list = (usersRes.data || []).slice().sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      );
      setUsers(list);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load users directory");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, loadActiveCompanies]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Summary counts
  const summary = useMemo(() => {
    const roles = { super_admin: 0, admin: 0, user: 0 };
    users.forEach((u) => {
      const r = (u.role || "user").toLowerCase().trim();
      if (roles[r] !== undefined) roles[r]++;
      else roles.user++;
    });
    return roles;
  }, [users]);

  // Available unique companies from active settings companies + users directory
  const availableCompanies = useMemo(() => {
    const set = new Set(activeCompanies);
    users.forEach((u) => {
      getUserCompanies(u).forEach((c) => set.add(c));
    });
    return Array.from(set).filter(Boolean).sort();
  }, [users, activeCompanies]);

  // Filtered directory (always sorted alphabetically A-Z by displayed Name)
  const filteredUsers = useMemo(() => {
    return users
      .filter((u) => {
        if (roleFilter && (u.role || "user").toLowerCase() !== roleFilter.toLowerCase()) return false;
        
        // Check if any of user's departments match the filter
        if (deptFilter) {
          const userDepts = u.departments && Array.isArray(u.departments) && u.departments.length > 0
            ? u.departments.map(d => (d || "").toLowerCase())
            : [(u.department || "").toLowerCase()];
          
          if (!userDepts.some(d => d === deptFilter.toLowerCase())) return false;
        }

        // Check if any of user's companies match the filter
        if (companyFilter) {
          const userComps = getUserCompanies(u).map(c => c.toLowerCase());
          if (!userComps.includes(companyFilter.toLowerCase())) return false;
        }
        
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          // Also search in all departments and companies
          const userDepts = u.departments && Array.isArray(u.departments) && u.departments.length > 0
            ? u.departments
            : [u.department];
          const deptMatch = userDepts.some(d => (d || "").toLowerCase().includes(q));
          const compMatch = getUserCompanies(u).some(c => (c || "").toLowerCase().includes(q));
          
          return (
            u.name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q) ||
            u.phone?.toLowerCase().includes(q) ||
            u.user_id?.toLowerCase().includes(q) ||
            deptMatch ||
            compMatch
          );
        }
        return true;
      })
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      );
  }, [users, roleFilter, deptFilter, companyFilter, searchQuery]);

  const { currentPage, setCurrentPage, pageSize, setPageSize, paginatedItems, totalItems } = usePagination(filteredUsers, 10, { sectionKey: "users_roles" });

  const handleDepartmentChange = (newDepts) => {
    setFormData((prev) => ({ ...prev, departments: newDepts }));
    if (newDepts && newDepts.length > 0) {
      setFormErrors((prev) => ({ ...prev, departments: null }));
    }
  };

  const handleCompanyChange = (newComps) => {
    setFormData((prev) => ({ ...prev, companies: newComps }));
    if (newComps && newComps.length > 0) {
      setFormErrors((prev) => ({ ...prev, companies: null }));
    }
  };

  const openAddModal = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      password: "",
      role: "user",
      departments: [],
      companies: [],
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const openEditModal = (targetUser) => {
    setEditingUser(targetUser);
    // Ensure we have departments array
    const userDepts = targetUser.departments && Array.isArray(targetUser.departments) && targetUser.departments.length > 0
      ? targetUser.departments
      : [targetUser.department || "Sales"];
    
    setFormData({
      name: targetUser.name || "",
      email: targetUser.email || "",
      phone: targetUser.phone || "",
      password: "",
      role: (targetUser.role || "user").toLowerCase(),
      departments: userDepts,
      companies: getUserCompanies(targetUser),
    });
    setFormErrors({});
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.departments || formData.departments.length === 0) {
      errors.departments = "Please select at least one department";
    }
    if (!formData.companies || formData.companies.length === 0) {
      errors.companies = "Please select at least one company";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      if (errors.departments && errors.companies) {
        toast.error("Please select at least one department and one company");
      } else if (errors.departments) {
        toast.error(errors.departments);
      } else if (errors.companies) {
        toast.error(errors.companies);
      }
      return;
    }

    setSubmitting(true);
    try {
      await api("post", "/users", formData);
      toast.success(`User ${formData.name} added successfully`);
      setShowAddModal(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await api("post", `/users/${resetTarget.user_id}/reset-password`);
      toast.success(res.data?.message || "Password reset email sent");
      setResetTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const handleToggleStatus = async (targetUser) => {
    if (!targetUser) return;
    try {
      const res = await api("post", `/users/${targetUser.user_id}/toggle-status`);
      toast.success(res.data?.message || "User status updated");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user status");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api("delete", `/users/${deleteTarget.user_id}`);
      toast.success(res.data?.message || `User '${deleteTarget.name}' deleted successfully`);
      setDeleteTarget(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreUser = async (targetUser) => {
    if (!targetUser) return;
    try {
      const res = await api("post", `/users/${targetUser.user_id}/restore`);
      toast.success(res.data?.message || `User '${targetUser.name}' restored successfully`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore user");
    }
  };

  const handleToggleMenu = (e, targetUser) => {
    e.stopPropagation();
    if (activeMenu?.user?.user_id === targetUser.user_id) {
      setActiveMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 185;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpwards = spaceBelow < menuHeight;
    setActiveMenu({
      user: targetUser,
      top: openUpwards ? rect.top - 4 : rect.bottom + 4,
      right: window.innerWidth - rect.right,
      openUpwards,
    });
  };

  const openUserPage = (u) => {
    if (onOpenUser) onOpenUser(u.user_id);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    const errors = {};
    if (!formData.departments || formData.departments.length === 0) {
      errors.departments = "Please select at least one department";
    }
    if (!formData.companies || formData.companies.length === 0) {
      errors.companies = "Please select at least one company";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      if (errors.departments && errors.companies) {
        toast.error("Please select at least one department and one company");
      } else if (errors.departments) {
        toast.error(errors.departments);
      } else if (errors.companies) {
        toast.error(errors.companies);
      }
      return;
    }

    setSubmitting(true);
    try {
      const payload = { 
        name: formData.name, 
        phone: formData.phone, 
        role: formData.role, 
        departments: formData.departments,
        companies: formData.companies,
      };
      if (formData.password) payload.password = formData.password;
      await api("put", `/users/${editingUser.user_id}`, payload);
      toast.success(`User ${formData.name} updated successfully`);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="content" data-testid="users-roles-page">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <h1>Users &amp; Roles</h1>
          <p className="muted">Manage team members, roles, departmental access, and assigned companies.</p>
        </div>
        {currentUserIsAdmin && (
          <button className="primary-btn compact" onClick={openAddModal} data-testid="add-user-button">
            <UserPlus size={15} /> + Add Team Member
          </button>
        )}
      </div>

      {error && <div className="error" style={{ marginBottom: 20 }}>{error}</div>}

      {/* 3 Roles Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 22 }}>
        {[
          { key: "super_admin", label: "SUPER ADMIN", count: summary.super_admin, testId: "role-card-super_admin" },
          { key: "admin", label: "ADMIN", count: summary.admin, testId: "role-card-admin" },
          { key: "user", label: "USER", count: summary.user, testId: "role-card-user" },
        ].map(({ key, label, count, testId }) => (
          <div
            key={key}
            className="panel"
            style={{
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#fff",
            }}
            data-testid={testId}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", letterSpacing: "0.4px" }}>
              {label}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>
              <b style={{ fontSize: 18, color: "var(--navy)", fontWeight: 800 }}>{count}</b> {count === 1 ? "user" : "users"}
            </span>
          </div>
        ))}
      </div>

      {/* Directory Section */}
      <section className="panel" data-testid="team-directory-panel">
        <div className="panel-title" style={{ flexWrap: "wrap", gap: 14 }}>
          <div>
            <h3>Team Directory <span className="count">{filteredUsers.length}</span></h3>
            <p className="muted">All registered team members and their system role, departments &amp; companies.{currentUserIsAdmin && " Click any row to view full user details."}</p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <select
              className="dept-select"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              data-testid="filter-role-select"
              style={{ fontSize: 12, padding: "7px 10px" }}
            >
              <option value="">All System Roles</option>
              <option value="super_admin">SUPER ADMIN</option>
              <option value="admin">ADMIN</option>
              <option value="user">USER</option>
            </select>

            <select
              className="dept-select"
              value={deptFilter}
              onChange={(e) => {
                setDeptFilter(e.target.value);
                setCurrentPage(1);
              }}
              data-testid="filter-dept-select"
              style={{ fontSize: 12, padding: "7px 10px" }}
            >
              <option value="">All Departments</option>
              {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>

            <select
              className="dept-select"
              value={companyFilter}
              onChange={(e) => {
                setCompanyFilter(e.target.value);
                setCurrentPage(1);
              }}
              data-testid="filter-company-select"
              style={{ fontSize: 12, padding: "7px 10px" }}
            >
              <option value="">All Companies</option>
              {availableCompanies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>

            <select
              className="dept-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              data-testid="filter-status-select"
              style={{ fontSize: 12, padding: "7px 10px" }}
            >
              <option value="all">Status: All (Active &amp; Deactivated)</option>
              <option value="active">Status: Active</option>
              <option value="inactive">Status: Inactive / Deactivated</option>
              <option value="deleted">Status: Deleted</option>
            </select>

            <div className="activity-search-box" style={{ width: 200, marginBottom: 0 }}>
              <Search size={14} />
              <input
                type="text"
                placeholder="Search by name, email, company…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                data-testid="user-search-input"
              />
            </div>

            <button className={`refresh-activity-btn ${loading ? "spinning" : ""}`} onClick={loadUsers}
              title="Refresh Team" data-testid="refresh-users-btn">
              <RotateCw size={14} />
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Companies</th>
                <th>Status</th>
                {currentUserIsAdmin && <th style={{ textAlign: "right", width: 70 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((u) => {
                const r = (u.role || "user").toLowerCase().trim();
                const canModifyThisUser = currentUserIsSuperAdmin || (currentUserIsAdmin && r !== "super_admin");
                const isDeleted = Boolean(u.is_deleted);
                const rc = roleColor(r);

                return (
                  <tr
                    key={u.user_id}
                    data-testid={`user-row-${u.user_id}`}
                    style={{
                      cursor: currentUserIsAdmin ? "pointer" : "default",
                      opacity: isDeleted ? 0.75 : 1,
                      background: isDeleted ? "#fef2f220" : undefined,
                    }}
                    onClick={() => currentUserIsAdmin && openUserPage(u)}
                  >
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div className="avatar" style={{ background: isDeleted ? "#e2e8f0" : rc.bg, color: isDeleted ? "#64748b" : rc.color, fontWeight: 800 }}>
                          {initials(u.name)}
                        </div>
                        <div>
                          <b style={{ color: isDeleted ? "#64748b" : "var(--navy)", textDecoration: isDeleted ? "line-through" : "none" }}>{u.name}</b>
                          {isDeleted && <small style={{ display: "block", color: "#dc2626", fontSize: 10.5, fontWeight: 600 }}>Deleted User</small>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: 12.5, color: "#1e293b" }}>{u.email}</span>
                      {u.phone && <small style={{ display: "block", color: "#64748b", fontSize: 11 }}>{u.phone}</small>}
                    </td>
                    <td>
                      <span style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: rc.bg, color: rc.color,
                      }}>
                        {formatRole(u.role)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(() => {
                          const depts = u.departments && Array.isArray(u.departments) && u.departments.length > 0
                            ? u.departments
                            : [u.department || "Sales"];
                          return depts.map((dept, idx) => (
                            <span key={idx} className={getDeptTagClass(dept)}>
                              <Building2 size={11} /> {dept}
                            </span>
                          ));
                        })()}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {getUserCompanies(u).map((comp, idx) => (
                          <span key={idx} className="company-chip" style={{ fontSize: 10.5, padding: "2px 6px" }}>
                            <Building size={10} /> {comp}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {isDeleted ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                          background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca",
                        }} data-testid={`user-status-deleted-${u.user_id}`}>
                          <Trash2 size={11} /> Deleted
                        </span>
                      ) : (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 20,
                          background: u.is_active === false ? "#fef2f2" : "#f0fdf4",
                          color: u.is_active === false ? "#b91c1c" : "#15803d",
                        }} data-testid={`user-status-${u.is_active === false ? "inactive" : "active"}-${u.user_id}`}>
                          {u.is_active === false ? <UserX size={11} /> : <UserCheck size={11} />}
                          {u.is_active === false ? "Inactive / Deactivated" : "Active"}
                        </span>
                      )}
                    </td>
                    {currentUserIsAdmin && (
                      <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        {canModifyThisUser ? (
                          <div className="user-action-menu-container">
                            <button
                              type="button"
                              className={`user-action-dots-btn ${activeMenu?.user?.user_id === u.user_id ? "active" : ""}`}
                              onClick={(e) => handleToggleMenu(e, u)}
                              data-testid={`user-menu-btn-${u.user_id}`}
                              title="Actions"
                            >
                              <MoreVertical size={16} />
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Super Admin</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredUsers.length === 0 && !loading && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)", fontSize: 13, fontWeight: 500 }}>
              No team members match your filters.
            </div>
          )}
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="team members"
        />
      </section>

      {/* ── Fixed User Action Menu Dropdown ── */}
      {activeMenu && activeMenu.user && (
        <div
          className="user-action-dropdown fixed"
          style={{
            top: activeMenu.top,
            right: activeMenu.right,
            transform: activeMenu.openUpwards ? "translateY(-100%)" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`user-dropdown-menu-${activeMenu.user.user_id}`}
        >
          {Boolean(activeMenu.user.is_deleted) ? (
            <button
              type="button"
              className="user-action-item restore"
              onClick={() => {
                const u = activeMenu.user;
                setActiveMenu(null);
                handleRestoreUser(u);
              }}
              data-testid={`restore-user-btn-${activeMenu.user.user_id}`}
            >
              <RotateCcw size={14} /> Restore User
            </button>
          ) : (
            <>
              <button
                type="button"
                className="user-action-item"
                onClick={() => {
                  const u = activeMenu.user;
                  setActiveMenu(null);
                  openEditModal(u);
                }}
                data-testid={`edit-user-btn-${activeMenu.user.user_id}`}
              >
                <Pencil size={14} style={{ color: "#64748b" }} /> Edit
              </button>

              <button
                type="button"
                className="user-action-item"
                onClick={() => {
                  const u = activeMenu.user;
                  setActiveMenu(null);
                  setResetTarget(u);
                }}
                data-testid={`reset-pwd-btn-${activeMenu.user.user_id}`}
              >
                <KeyRound size={14} style={{ color: "#b45309" }} /> Reset Password
              </button>

              {activeMenu.user.user_id !== user?.user_id && (
                <button
                  type="button"
                  className="user-action-item"
                  onClick={() => {
                    const u = activeMenu.user;
                    setActiveMenu(null);
                    handleToggleStatus(u);
                  }}
                  data-testid={`toggle-status-btn-${activeMenu.user.user_id}`}
                >
                  {activeMenu.user.is_active === false ? (
                    <>
                      <UserCheck size={14} style={{ color: "#16a34a" }} /> Activate
                    </>
                  ) : (
                    <>
                      <UserX size={14} style={{ color: "#b91c1c" }} /> Deactivate
                    </>
                  )}
                </button>
              )}

              {activeMenu.user.user_id !== user?.user_id && (activeMenu.user.email || "").toLowerCase() !== "admin@pinakisolutions.com" && (
                <>
                  <div className="user-action-divider" />
                  <button
                    type="button"
                    className="user-action-item danger"
                    onClick={() => {
                      const u = activeMenu.user;
                      setActiveMenu(null);
                      setDeleteTarget(u);
                    }}
                    data-testid={`delete-user-btn-${activeMenu.user.user_id}`}
                  >
                    <Trash2 size={14} /> Delete User
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}


      {/* â”€â”€ Reset Password Confirmation Modal â”€â”€ */}
      {resetTarget && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ width: "min(460px, 95%)" }} data-testid="reset-password-modal">
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fef3c7", color: "#b45309", display: "grid", placeItems: "center" }}>
                  <KeyRound size={16} />
                </div>
                <div>
                  <h3 style={{ margin: "0 0 2px", fontSize: 15 }}>Reset Password</h3>
                  <small className="muted">Administrative password reset</small>
                </div>
              </div>
              <button type="button" className="icon-btn" onClick={() => setResetTarget(null)} style={{ padding: 4 }} disabled={resetting}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "4px 0 16px", fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
              <p style={{ margin: "0 0 14px" }}>
                A new secure temporary password will be generated and emailed directly to{" "}
                <b style={{ color: "#0f172a" }}>{resetTarget.email}</b>. The user will be required to change it on their next login.
              </p>

              <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 12 }}>
                {[
                  ["Name:", resetTarget.name],
                  ["User ID:", resetTarget.user_id],
                  ["Email:", resetTarget.email],
                  ["Role:", resetTarget.role?.toUpperCase()],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                    <span style={{ color: "#94a3b8", width: 80 }}>{k}</span>
                    <b style={{ color: "#0f172a", fontFamily: k === "User ID:" ? "monospace" : undefined }}>{v}</b>
                  </div>
                ))}
              </div>

              <div style={{ background: "#fef3c7", borderLeft: "3px solid #f59e0b", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#78350f" }}>
                <b>Security notice:</b> The temporary password is never displayed on screen or logged. It is delivered only to the user's registered email address.
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="outline-btn" onClick={() => setResetTarget(null)} disabled={resetting}>Cancel</button>
              <button
                className="primary-btn"
                style={{ background: "#b45309", borderColor: "#b45309" }}
                onClick={handleResetPassword}
                disabled={resetting}
                data-testid="confirm-reset-pwd-btn"
              >
                <KeyRound size={13} /> {resetting ? "Sendingâ€¦" : "Confirm & Send Reset Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* â”€â”€ Add Team Member Modal â”€â”€ */}
      {showAddModal && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <form
            className="modal"
            onSubmit={handleCreateUser}
            data-testid="add-user-modal-form"
            style={{ width: "min(620px, 95%)", maxWidth: 620, padding: "22px 24px", overflow: "visible" }}
          >
            <div className="modal-head" style={{ marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
              <div>
                <h3 style={{ margin: "0 0 3px", fontSize: 17, color: "var(--navy)", fontWeight: 700 }}>Add Team Member</h3>
                <small className="muted" style={{ fontSize: 12 }}>Create an account with a System Role and Departmental access</small>
              </div>
              <button type="button" className="icon-btn" onClick={() => setShowAddModal(false)} style={{ padding: 6 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
              <div>
                <label className="dispatch-label">Full Name <span style={{ color: "var(--red)" }}>*</span></label>
                <input
                  type="text"
                  className="dispatch-control"
                  placeholder="e.g. Rahul Sharma"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                  data-testid="add-user-name-input"
                />
              </div>

              <div>
                <label className="dispatch-label">Email Address <span style={{ color: "var(--red)" }}>*</span></label>
                <input
                  type="email"
                  className="dispatch-control"
                  placeholder="e.g. rahul@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="add-user-email-input"
                />
              </div>

              <div>
                <label className="dispatch-label">Phone Number</label>
                <input
                  type="text"
                  className="dispatch-control"
                  placeholder="e.g. +91 98200 12345"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="add-user-phone-input"
                />
              </div>

              <div>
                <label className="dispatch-label">System Role <span style={{ color: "var(--red)" }}>*</span></label>
                <select
                  className="dispatch-control"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="add-user-role-select"
                >
                  <option value="user">USER (Standard)</option>
                  <option value="admin">ADMIN (Managerial)</option>
                  {currentUserIsSuperAdmin && <option value="super_admin">SUPER ADMIN</option>}
                </select>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label className="dispatch-label">Initial Password <span style={{ color: "var(--red)" }}>*</span></label>
                <PasswordInput
                  className="dispatch-control"
                  placeholder="At least 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="add-user-password-input"
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label className="dispatch-label">Assigned Departments <span style={{ color: "var(--red)" }}>*</span></label>
                <MultiSelectDepartments
                  value={formData.departments}
                  onChange={handleDepartmentChange}
                  departments={DEPARTMENTS}
                  required={true}
                  placeholder="Select departments..."
                  hasError={!!formErrors.departments}
                  testId="add-user-dept-select"
                />
                {formErrors.departments && (
                  <span style={{ color: "var(--red)", fontSize: 12, marginTop: 4, display: "block" }}>
                    {formErrors.departments}
                  </span>
                )}
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label className="dispatch-label">Assigned Companies <span style={{ color: "var(--red)" }}>*</span></label>
                <MultiSelectCompanies
                  value={formData.companies}
                  onChange={handleCompanyChange}
                  required={true}
                  placeholder="Select companies..."
                  hasError={!!formErrors.companies}
                  testId="add-user-company-select"
                />
                {formErrors.companies && (
                  <span style={{ color: "var(--red)", fontSize: 12, marginTop: 4, display: "block" }}>
                    {formErrors.companies}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
              <button type="button" className="outline-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={submitting} data-testid="submit-add-user-btn">
                {submitting ? "Saving…" : "Create Member"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Edit Team Member Modal ── */}
      {editingUser && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <form
            className="modal"
            onSubmit={handleUpdateUser}
            data-testid="edit-user-modal-form"
            style={{ width: "min(620px, 95%)", maxWidth: 620, padding: "22px 24px", overflow: "visible" }}
          >
            <div className="modal-head" style={{ marginBottom: 18, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
              <div>
                <h3 style={{ margin: "0 0 3px", fontSize: 17, color: "var(--navy)", fontWeight: 700 }}>Edit Team Member</h3>
                <small className="muted" style={{ fontSize: 12 }}>{editingUser.email} · {editingUser.user_id}</small>
              </div>
              <button type="button" className="icon-btn" onClick={() => setEditingUser(null)} style={{ padding: 6 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
              <div>
                <label className="dispatch-label">Full Name <span style={{ color: "var(--red)" }}>*</span></label>
                <input
                  type="text"
                  className="dispatch-control"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                  data-testid="edit-user-name-input"
                />
              </div>

              <div>
                <label className="dispatch-label">Phone Number</label>
                <input
                  type="text"
                  className="dispatch-control"
                  placeholder="e.g. +91 98200 12345"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="edit-user-phone-input"
                />
              </div>

              <div>
                <label className="dispatch-label">System Role <span style={{ color: "var(--red)" }}>*</span></label>
                <select
                  className="dispatch-control"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="edit-user-role-select"
                >
                  <option value="user">USER</option>
                  <option value="admin">ADMIN</option>
                  {currentUserIsSuperAdmin && <option value="super_admin">SUPER ADMIN</option>}
                </select>
              </div>

              <div>
                <label className="dispatch-label">Reset Password (Optional)</label>
                <PasswordInput
                  className="dispatch-control"
                  placeholder="Leave blank to keep unchanged"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  minLength={6}
                  data-testid="edit-user-password-input"
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label className="dispatch-label">Assigned Departments <span style={{ color: "var(--red)" }}>*</span></label>
                <MultiSelectDepartments
                  value={formData.departments}
                  onChange={handleDepartmentChange}
                  departments={DEPARTMENTS}
                  required={true}
                  placeholder="Select departments..."
                  hasError={!!formErrors.departments}
                  testId="edit-user-dept-select"
                />
                {formErrors.departments && (
                  <span style={{ color: "var(--red)", fontSize: 12, marginTop: 4, display: "block" }}>
                    {formErrors.departments}
                  </span>
                )}
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label className="dispatch-label">Assigned Companies <span style={{ color: "var(--red)" }}>*</span></label>
                <MultiSelectCompanies
                  value={formData.companies}
                  onChange={handleCompanyChange}
                  required={true}
                  placeholder="Select companies..."
                  hasError={!!formErrors.companies}
                  testId="edit-user-company-select"
                />
                {formErrors.companies && (
                  <span style={{ color: "var(--red)", fontSize: 12, marginTop: 4, display: "block" }}>
                    {formErrors.companies}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
              <button type="button" className="outline-btn" onClick={() => setEditingUser(null)}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={submitting} data-testid="submit-edit-user-btn">
                {submitting ? "Updating..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Soft Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }} data-testid="delete-user-modal-backdrop">
          <div className="modal" style={{ width: "min(460px, 95%)" }} data-testid="delete-user-modal">
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fef2f2", color: "#dc2626", display: "grid", placeItems: "center" }}>
                  <Trash2 size={16} />
                </div>
                <div>
                  <h3 style={{ margin: "0 0 2px", fontSize: 15 }}>Delete User</h3>
                  <small className="muted">Soft delete confirmation</small>
                </div>
              </div>
              <button className="icon-btn" onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ padding: 4 }} data-testid="delete-user-close-btn">
                <X size={18} />
              </button>
            </div>

            <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: 18 }}>
              <p style={{ margin: "0 0 14px", fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>
                Are you sure you want to delete this user?
              </p>

              <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 12 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                  <span style={{ color: "#64748b", width: 90, fontWeight: 600 }}>User:</span>
                  <b style={{ color: "#0f172a" }}>{deleteTarget.name}</b>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                  <span style={{ color: "#64748b", width: 90, fontWeight: 600 }}>Email:</span>
                  <b style={{ color: "#0f172a" }}>{deleteTarget.email}</b>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <span style={{ color: "#64748b", width: 90, fontWeight: 600 }}>Role / Dept:</span>
                  <b style={{ color: "#0f172a" }}>
                    {formatRole(deleteTarget.role)} · {(() => {
                      const depts = deleteTarget.departments && Array.isArray(deleteTarget.departments) && deleteTarget.departments.length > 0
                        ? deleteTarget.departments
                        : [deleteTarget.department || "Sales"];
                      return depts.join(" · ");
                    })()}
                  </b>
                </div>
              </div>

              <div style={{ background: "#fef2f2", borderLeft: "3px solid #ef4444", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#991b1b" }}>
                <b>Soft Delete Notice:</b> This user will no longer appear in the active user list and will be blocked from logging in. All historical tasks, leads, clients, orders, and reports created by this user will remain fully preserved in history.
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="outline-btn" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-user-cancel-btn">
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                style={{ background: "#dc2626", borderColor: "#dc2626" }}
                onClick={handleDeleteUser}
                disabled={deleting}
                data-testid="delete-user-confirm-btn"
              >
                <Trash2 size={13} /> {deleting ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

